import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { EnaioUtils } from 'src/modules/enaio/misc/enaio-utils';
import { EnaioHelper } from 'src/modules/enaio/shared/enaio-helper.model';
import { EnaioGuiControlType } from 'src/modules/enaio/shared/EnaioGuiControlType';
import { EnaioGuiField } from 'src/modules/enaio/shared/EnaioGuiField';
import { EnaioWorkflowBasicDataType } from 'src/modules/enaio/shared/EnaioWorkflowBasicDataType';
import { EnaioWorkItemParameter } from 'src/modules/enaio/shared/EnaioWorkItemParameter';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { FrontendFormDefinition } from 'src/modules/sm-base/models/frontend-form-definition.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { EnaioGuiFieldReadOnlyType } from '../shared/EnaioGuiFieldReadOnlyType';
import { EnaioGuiPage } from '../shared/EnaioGuiPage';

export class FrontendEnaioMapper {

    static scaleFactor = 2.2;

    static enaioToFrontendForm(fields: EnaioGuiField[], app: MainAppService, translationPrefix: string, wfParams: EnaioWorkItemParameter[] = null, toUpperLeft = true, responsive = false, formWidth?: number, formHeight?: number): FrontendFormDefinition {
        let minX = toUpperLeft ? Math.max(0, Math.min(...Utils.arrayExplode(fields, f => Utils.arrayWithoutNull([f.isLabelVisible && f.controlType2 != EnaioGuiControlType.checkBox && f.controlType2 != EnaioGuiControlType.radioButton ? f.fieldPos.left : null, f.inputPos.left])))) : 0;
        let minY = toUpperLeft ? Math.max(0, Math.min(...Utils.arrayExplode(fields, f => Utils.arrayWithoutNull([f.isLabelVisible && f.controlType2 != EnaioGuiControlType.checkBox && f.controlType2 != EnaioGuiControlType.radioButton ? f.fieldPos.top : null, f.inputPos.top])))) : 0;
       
        let fields2: EnaioGuiField[] = [];
        for (let field of fields) {
            if (Utils.isNoe(field.wfPageId)) {
                fields2.push(field);    
            }
            else {
                let parent: EnaioGuiPage = null;
                for (let field2 of fields) {
                    if (field2.pages != null) {
                        for (let page of field2.pages) {
                            if (page.pageId == field.wfPageId) {
                                parent = page;
                                break;
                            }
                        }
                    }
                }
                if (parent != null) {
                    parent.fields ??= [];
                    parent.fields.push(field);
                }
            }
        }

        let result = new FrontendFormDefinition(Utils.arraySortBy(Utils.arrayWithoutNull(fields2.map(f =>
            FrontendEnaioMapper.enaioToFrontendField(f, wfParams, app, translationPrefix, -minX, -minY, responsive))), f => f.tabOrder), !responsive);
        
            result.mandatoryFieldsRedAsterisk = true;

        let fw = formWidth != null ? (formWidth - minX) * FrontendEnaioMapper.scaleFactor : null;
        let fh = formHeight != null ? (formHeight - minY) * FrontendEnaioMapper.scaleFactor : null;

        if (fw != null || fh != null) {
            for (let field of result.fields) {
                if (fw != null && field.fixedControlBounds != null && field.fixedControlBounds.x >= fw || 
                    fh != null && field.fixedControlBounds != null && field.fixedControlBounds.y >= fh) {
                        field.hasControl = false;
                }
                if (fw != null && field.fixedLabelBounds != null && field.fixedLabelBounds.x >= fw || 
                    fh != null && field.fixedLabelBounds != null && field.fixedLabelBounds.y >= fh) {
                        field.hasLabel = false;
                }
            }        
        }

        return result;
    }

    static enaioToFrontendField(field: EnaioGuiField, wfParams: EnaioWorkItemParameter[], app: MainAppService, translationPrefix: string, shiftX = 0, shiftY = 0, responsive = false): FrontendFieldDefinition {
        let result = new FrontendFieldDefinition(Utils.stringDef(field.wfFieldId, field.internalName), app.t(translationPrefix + "." + field.name, null, field.name), FrontendFieldType.text);
        result.userDefinedData = { originalCaption: field.name }; //Caption kann per Skript geändert werden. Wir benötigen die orginale Caption um das richtige Change-Event zu ermitteln & Multilang
        result.tabOrder = field.tabOrder;
        result.fixedLabelBounds = field.fieldPos.toBounds(FrontendEnaioMapper.scaleFactor, shiftX * FrontendEnaioMapper.scaleFactor, shiftY * FrontendEnaioMapper.scaleFactor);
        result.fixedControlBounds = field.inputPos.toBounds(FrontendEnaioMapper.scaleFactor, shiftX * FrontendEnaioMapper.scaleFactor, shiftY * FrontendEnaioMapper.scaleFactor);
        result.hasLabel = field.needsLabel && field.isLabelVisible && result.fixedLabelBounds.width > 0 && result.fixedLabelBounds.height > 0;
        result.hasControl = field.isControlVisible && result.fixedControlBounds.width > 0 && result.fixedControlBounds.height > 0;
        result.enaioSource = field;
        result.mandatory = field.mandatory;
        result.maxLength = field.maxLength;
        result.upperCase = field.upperCase;
        result.labelRightAligned = !responsive && field.isLabelRightAligned;
        result.enabled = field.readOnly == EnaioGuiFieldReadOnlyType.none;
        result.regexMask = field.regularExpression;
        result.listItems = field.listItems != null ? [new FrontendFieldListItem("", ""), ...field.listItems
            .map(item => EnaioUtils.guiFieldListItemToFrontend(item, field.listUseItemValue, app.t(translationPrefix + "." + field.name + "_item_" + item.text, null, item.text)))] : [];
        if (field.listOrder) {
            result.listItems = Utils.arraySortBy(result.listItems, li => li.label);
        }
        result.enaioSectionName = field.sectionName;

        switch (field.controlType2) {
            case EnaioGuiControlType.button:
                result.type = FrontendFieldType.button;
                result.hasControl = true;
                result.hasLabel = false;
                result.fixedControlBounds = result.fixedLabelBounds;
                break;
            case EnaioGuiControlType.checkBox:
                result.fixedControlBounds.width *= 1.4;
                result.type = FrontendFieldType.checkBox;
                break;
            case EnaioGuiControlType.radioButton:
                result.fixedControlBounds.width *= 1.4;
                result.type = FrontendFieldType.radioButton;
                result.radioButtonGroup = field.radioButtonGroup ?? "x";
                result.radioButtonValue = field.radioButtonValue;
                break;
            case EnaioGuiControlType.datePicker:
                result.type = FrontendFieldType.datePicker;
                break;
            case EnaioGuiControlType.table:
                result.type = FrontendFieldType.table;
                let wfParam = wfParams.find(param => param.formFieldId === field.wfFieldId);
                let recordMembers = wfParam?.value.types != null && Utils.getOwnPropertyNames(wfParam.value.types).length > 0 ? wfParam.value.types[Utils.getOwnPropertyNames(wfParam.value.types)[0]].recordMembers : [];
                let allColsWidth = field.tableColumns.reduce((prev, column) => prev + column.colWidth, 0);
                result.tableColumns = Utils.mapTwo(field.tableColumns, recordMembers,
                    (item, recordMember) => EnaioUtils.guiFieldTableColumnToFrontend(result, item, recordMember, result.fixedControlBounds.width, allColsWidth));
                for (let tableColumn of result.tableColumns) {
                    if (tableColumn.editor instanceof FrontendFieldDefinition && tableColumn.editor.listItems != null) {
                        for (let listItem of tableColumn.editor.listItems) {
                            listItem.label = app.t(translationPrefix + "." + field.name + "_column_" + tableColumn.caption + "_item_" + listItem.label, null, listItem.label);        
                        }
                    }
                    tableColumn.caption = app.t(translationPrefix + "." + field.name + "_column_" + tableColumn.caption, null, tableColumn.caption);        
                }
                result.fixedLabelBounds.x += 5 * FrontendEnaioMapper.scaleFactor;
                result.fixedLabelBounds.y -= 3 * FrontendEnaioMapper.scaleFactor;
                result.fixedControlBounds.x -= 2 * FrontendEnaioMapper.scaleFactor;
                result.fixedControlBounds.y -= 2 * FrontendEnaioMapper.scaleFactor;
                result.fixedControlBounds.width += 5 * FrontendEnaioMapper.scaleFactor;
                result.fixedControlBounds.height += 8 * FrontendEnaioMapper.scaleFactor;
                result.generateEmptyTableItem = (): any => wfParam != null ? wfParam.generateRecordItem() : {};
                result.fullScreenRowEditorOnMobile = true;
                break;
            case EnaioGuiControlType.comboBox:
                result.type = field.listMultiSelect ? FrontendFieldType.comboBoxMulti : FrontendFieldType.comboBox;
                result.dropdownEditable = false;
                result.fixedControlBounds.width += 13 * FrontendEnaioMapper.scaleFactor;
                break;
            case EnaioGuiControlType.textArea:
                result.type = FrontendFieldType.textArea;
                break;
            case EnaioGuiControlType.textBox:
                if (result.regexMask !== "" && result.regexMask != null) {
                    result.type = FrontendFieldType.maskField;
                }
                break;
            case EnaioGuiControlType.groupBox:
                return null;
         /*       result.type = FrontendFieldType.groupBox;
                result.hasControl = true;
                result.hasLabel = false;
                result.fixedControlBounds = result.fixedLabelBounds;
                result.fixedControlBounds.y -= 25;
                result.fixedControlBounds.width -= 30;
                result.fixedControlBounds.height -= 50;
                break;
           */ case EnaioGuiControlType.pageControl:
                result.type = FrontendFieldType.pageControl;
                result.fixedControlBounds = result.fixedLabelBounds;
                result.children = field.pages.map(page => new FrontendFieldDefinition(page.pageId, page.name, FrontendFieldType.page, { children: page.fields.map(f => this.enaioToFrontendField(f, wfParams, app, translationPrefix, shiftX, shiftY + 20, responsive))}))
                result.hasControl = true;
                result.hasLabel = false;
                break;
            case EnaioGuiControlType.label:
                result.type = FrontendFieldType.text;
                result.hasControl = false;
                break;
            default:
                console.log("Unbekannter ControlType: " + field.controlType2 + " für Feld " + field.name);
        }

        if (responsive) {
            result.fixedControlBounds = null;
            result.fixedLabelBounds = null;
        }

        return result;
    }

    static frontendToEnaioValue(from: FrontendFieldDefinition, allFields: FrontendFieldDefinition[], allParams: EnaioWorkItemParameter[]): void {
        let to: EnaioWorkItemParameter;
        if (from.type === FrontendFieldType.radioButton) {
            let from2 = allFields.find(item => item.radioButtonGroup === from.radioButtonGroup && item.radioButtonValue == 0);
            if (from2 == null) {
                return; 
               // throw new Error("Keine Radio Button Gruppe mit dem Namen " + from.radioButtonGroup + " gefunden");
            }
            to = allParams.find(param => param.formFieldId === from2.id);
        }
        else {
            to = allParams.find(param => param.formFieldId === from.id);
        }

        if (to == null) {
            return;
        }

        let value = from.value instanceof Date ? Utils.dateFormatDefaultDate(from.value) : from.value;

        to.value.value = value;

        if (to.value.value instanceof FrontendFieldListItem) {
            to.value.value = to.value.value.label;
        }
        if (Utils.isArray(to.value.value) && to.value.value.length > 0) {
            if (to.value.value[0] instanceof FrontendFieldListItem) {
                to.value.value = (to.value.value as FrontendFieldListItem[]).map(v => v.label);
            }
            else if (to.value.dataType.basic === EnaioWorkflowBasicDataType.array) {
                to.value.value = Utils.cloneDeep(to.value.value);
                for (let row of (to.value.value as any[])) {
                    for (let key of Utils.getOwnPropertyNames(row)) {
                        row[key] = row[key] instanceof Date ? Utils.dateFormatDefaultDate(row[key] as Date) : row[key];
                    }
                }
            }
        }
        //PRAGMATIC (zukünftig konfigurierbar)
        if (to.value.value instanceof Date && (to.name === "AbreiseAm" || to.name === "RueckkehrAm" || to.name === "start" || to.name === "ende")) {
            to.value.value = EnaioHelper.toString(to.value.value);
        }
        if (Utils.isBool(to.value.value) && to.value.dataType.basic === EnaioWorkflowBasicDataType.integer) {
            to.value.value = Utils.toNumber(to.value.value);
        }
    }

}
